import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import styles from './FAQAccordion.module.css';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "What services does SFrays offer?",
    answer: "We specialize in digital transformation, custom software development, cloud solutions, and strategic consulting. Our team works closely with you to understand your unique challenges and deliver tailored solutions that drive real business value."
  },
  {
    question: "How long does a typical project take?",
    answer: "Project timelines vary based on scope and complexity. A simple website might take 4-6 weeks, while enterprise solutions can range from 3-12 months. We'll provide a detailed timeline after understanding your specific requirements during our initial consultation."
  },
  {
    question: "What is your pricing structure?",
    answer: "We offer flexible pricing models including fixed-price projects, time and materials, and retainer-based engagements. Every project is unique, so we customize our pricing to match your budget and goals. Contact us for a personalized quote."
  },
  {
    question: "Do you provide ongoing support after launch?",
    answer: "Absolutely! We believe in long-term partnerships. We offer comprehensive maintenance packages, 24/7 technical support, performance monitoring, and continuous optimization to ensure your solution stays current and performs optimally."
  },
  {
    question: "Can you work with our existing team?",
    answer: "Yes! We love collaborating with in-house teams. Whether you need to augment your team with specialized skills, provide training, or work alongside your developers, we're flexible and adaptable to your working style and processes."
  },
  {
    question: "What makes SFrays different from other agencies?",
    answer: "We combine technical excellence with a genuine partnership approach. We don't just deliver code – we invest in understanding your business, anticipate challenges, and provide strategic guidance. Our clients become long-term partners, not just project numbers."
  }
];

export function FAQAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className={styles.accordion}>
      {faqs.map((faq, index) => (
        <motion.div
          key={index}
          className={styles.accordionItem}
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
        >
          <motion.button
            className={styles.accordionHeader}
            onClick={() => toggleAccordion(index)}
            whileHover={{ backgroundColor: '#fff5f0' }}
          >
            <span className={styles.question}>{faq.question}</span>
            <motion.div
              animate={{ rotate: openIndex === index ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className={styles.iconWrapper}
            >
              <ChevronDown size={20} />
            </motion.div>
          </motion.button>
          
          <AnimatePresence>
            {openIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className={styles.accordionContent}
              >
                <p className={styles.answer}>{faq.answer}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}
    </div>
  );
}
